import { useMutation } from '@tanstack/react-query';
import { UpgradeProposalFormData, UpgradeProposalGenerationResponse } from './types';

const PROPOSAL_API_URL =
	'https://script.google.com/macros/s/AKfycbyC-XSu0SXNJpImCIRwSSbXMKcXL8nUTpso09UgyEPF5MPmVQy0Y-ieWY3XZF1Zfc9W4w/exec';

/**
 * Helper to format numbers as comma-separated strings for the API
 */
const formatForApi = (value: number | undefined): string => {
	const num = value ?? 0;
	return num.toLocaleString('en-US', {
		maximumFractionDigits: 2,
		minimumFractionDigits: 2,
	});
};

/**
 * Hook to generate upgrade proposals using Google Apps Script
 */
export function useUpgradeProposalGeneration() {
	return useMutation<UpgradeProposalGenerationResponse, Error, UpgradeProposalFormData>({
		mutationFn: async (payload: UpgradeProposalFormData) => {
			// Build payload structure for upgrade proposals
			const upgradePayload = {
				// Proposal type and metadata
				proposalType: payload.proposalType,
				isUpgradeProposal: true,
				// Plan and pricing fields
				websiteUpgrade: payload.websiteUpgrade ?? '',
				totalAmountDueToday: formatForApi(payload.totalAmountDueToday),
				billingCycle: payload.billingCycle ?? 'Annual',
				monthsUntilRenewal: payload.monthsUntilRenewal ?? 0,
				renewalDate: payload.renewalDate ?? '',
				websiteAddon: payload.websiteAddon ?? '',
				// Custom proposal fields (only for custom type)
				prospectDM: payload.prospectName ?? '',
				prospectDMTitle: payload.prospectTitle ?? '',
				prospectCompany: payload.companyName ?? '',
				// Add-ons
				fbAdsChecked: payload.fbAdsChecked ?? false,
				fbAdsPremiumChecked: payload.fbPremiumChecked ?? false,
				gLSAChecked: payload.gLSAChecked ?? false,
				customContentChecked: payload.customContentChecked ?? false,
				customContentNonprofit: payload.customContentNonprofitChecked ?? false,
			};

			const response = await fetch(PROPOSAL_API_URL, {
				body: JSON.stringify(upgradePayload),
				headers: {
					'Content-Type': 'text/plain',
				},
				method: 'POST',
			});

			if (!response.ok) {
				throw new Error(`Failed to generate upgrade proposal: ${response.statusText}`);
			}

			const data = await response.json();
			return data;
		},
	});
}
